import type { ReactNode } from 'react';
import { Link } from 'react-router';
import * as HoverCard from '@radix-ui/react-hover-card';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button, SkeletonBlock, TextLink, cn } from '@/ui';

/**
 * A link that previews its record on hover or keyboard focus, so a glance at a customer, ride or
 * vehicle never costs a page switch. A click still opens the page; touch screens just follow the
 * link. `preview` renders only while the card is open, so nothing is fetched until then.
 */
export function PeekLink({ to, preview, className, children }: { to: string; preview: () => ReactNode; className?: string; children: ReactNode }) {
  return (
    <HoverCard.Root openDelay={350} closeDelay={120}>
      <HoverCard.Trigger asChild>
        <TextLink to={to} className={className}>
          {children}
        </TextLink>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 w-[22rem] max-w-[calc(100vw-24px)] rounded-lg border border-line bg-surface font-sans text-sm text-ink shadow-pop outline-none"
        >
          {preview()}
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}

/** The card's frame: a title row, the facts, and a way to the full page. */
export function PeekCard({ title, badges, to, loading, error, children }: { title: ReactNode; badges?: ReactNode; to: string; loading?: boolean; error?: boolean; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <span className="min-w-0 flex-1 truncate font-display text-base font-semibold text-heading">{title}</span>
        {badges}
      </div>
      <div className="px-3 py-2.5">{loading ? <SkeletonBlock /> : error ? <p className="m-0 text-ink-muted">{t('peek.unavailable')}</p> : children}</div>
      <div className="flex justify-end border-t border-line px-2 py-1.5">
        <Button asChild variant="ghost" size="sm">
          <Link to={to}>
            {t('peek.open')}
            <ArrowRight aria-hidden />
          </Link>
        </Button>
      </div>
    </div>
  );
}

/** Label/value pairs in two tight columns. */
export function PeekFacts({ items }: { items: { label: string; value: ReactNode; wide?: boolean }[] }) {
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2">
      {items.map((i) => (
        <div key={i.label} className={cn('min-w-0', i.wide && 'col-span-2')}>
          <dt className="text-xs font-bold text-ink-muted">{i.label}</dt>
          <dd className="m-0 truncate tabular-nums text-ink">{i.value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
